import React, { useState, useEffect } from "react";
import Spreadsheet from "react-spreadsheet";
import { FaSearchPlus, FaSearchMinus, FaPlus } from "react-icons/fa";

function DefinedField() {
  const [data, setData] = useState([
    [
      { value: "Activity", readOnly: true },
      { value: "Commodity", readOnly: true },
      { value: "Factor", readOnly: true },
      { value: "Domestic Institutions", readOnly: true },
      { value: "Government", readOnly: true },
      { value: "Tax", readOnly: true },
      { value: "Saving-Investment", readOnly: true },
      { value: "Rest of the world", readOnly: true }
    ],
    [
      { value: "", readOnly: false },
      { value: "", readOnly: true }, // Commodity is readonly and replica of Activity
      { value: "", readOnly: false },
      { value: "", readOnly: false },
      { value: "", readOnly: false },
      { value: "", readOnly: false },
      { value: "", readOnly: false },
      { value: "", readOnly: false }
    ]
  ]);

  const [zoomLevel, setZoomLevel] = useState(100);

  const handleChange = (newData) => {
    // Update commodity column to be replica of activity column
    const updatedData = newData.map((row, rowIndex) => {
      if (rowIndex === 0) return row; // Keep header row unchanged
      
      return row.map((cell, colIndex) => {
        if (colIndex === 1) { // Commodity column (index 1)
          return {
            value: newData[rowIndex][0].value, // Copy from Activity column
            readOnly: true
          };
        }
        return cell;
      });
    });
    
    setData(updatedData);
  };

  const handleAddRow = () => {
    const newRow = [
      { value: "", readOnly: false },
      { value: "", readOnly: true }, // Will be updated by handleChange
      { value: "", readOnly: false },
      { value: "", readOnly: false },
      { value: "", readOnly: false },
      { value: "", readOnly: false },
      { value: "", readOnly: false },
      { value: "", readOnly: false }
    ];
    
    setData(prevData => {
      const newData = [...prevData, newRow];
      // Update commodity column for the new row
      return newData.map((row, rowIndex) => {
        if (rowIndex === 0) return row; // Keep header row unchanged
        
        return row.map((cell, colIndex) => {
          if (colIndex === 1) { // Commodity column
            return {
              value: newData[rowIndex][0].value,
              readOnly: true
            };
          }
          return cell;
        });
      });
    });
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 10, 400));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 10, 10));
  };

  const handleZoomReset = () => {
    setZoomLevel(100);
  };

  const handleSliderChange = (e) => {
    setZoomLevel(parseInt(e.target.value));
  };

  // Keyboard shortcuts for zoom
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "=") {
        e.preventDefault();
        handleZoomIn();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "-") {
        e.preventDefault();
        handleZoomOut();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "0") {
        e.preventDefault();
        handleZoomReset();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div style={{ 
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      backgroundColor: "#f8f9fa"
    }}>
      <div style={{
        padding: "20px 20px 0 20px",
        flexShrink: 0
      }}>
        <h2 style={{ 
          textAlign: "center", 
          marginBottom: 20, 
          fontFamily: "Arial, sans-serif",
          color: "#2c3e50",
          fontSize: "24px",
          fontWeight: "600"
        }}>
          Defined Field
        </h2>
      </div>

      <div style={{
        flex: 1,
        padding: "0 20px 20px 20px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        position: "relative"
      }}>
        <div style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          border: "1px solid #e1e5e9",
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }}>
          <div style={{
            flex: 1,
            overflow: "auto",
            padding: "20px",
            position: "relative"
          }}>
            <div style={{
              transform: `scale(${zoomLevel / 100})`,
              transformOrigin: "top left",
              width: `${100 / (zoomLevel / 100)}%`,
              height: `${100 / (zoomLevel / 100)}%`,
              minHeight: "100%",
              position: "relative"
            }}>
              <Spreadsheet
                data={data}
                onChange={handleChange}
                darkMode={false}
                columnLabels={false}
                rowLabels={false}
                className="custom-spreadsheet"
              />
            </div>
          </div>
        </div>

        {/* Add Row Button */}
        <div style={{
          position: "absolute",
          bottom: "100px",
          right: "30px",
          zIndex: 1000
        }}>
          <button
            onClick={handleAddRow}
            style={{
              background: "#0366d6",
              border: "none",
              borderRadius: "6px",
              padding: "12px 16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => e.target.style.background = "#0256b3"}
            onMouseLeave={(e) => e.target.style.background = "#0366d6"}
            title="Add New Row"
          >
            <FaPlus size={14} />
            Add Row
          </button>
        </div>

        {/* Excel-style Zoom Slider at Bottom Right */}
        <div 
          className="zoom-controls"
          style={{
            position: "absolute",
            bottom: "30px",
            right: "30px",
            background: "white",
            border: "1px solid #d0d7de",
            borderRadius: "6px",
            padding: "8px 12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            zIndex: 1000
          }}
        >
          <button
            onClick={handleZoomOut}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              borderRadius: "3px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#24292f",
              fontSize: "12px"
            }}
            onMouseEnter={(e) => e.target.style.background = "#f6f8fa"}
            onMouseLeave={(e) => e.target.style.background = "transparent"}
            title="Zoom Out"
          >
            <FaSearchMinus size={12} />
          </button>
          
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}>
            <input
              type="range"
              min="10"
              max="400"
              value={zoomLevel}
              onChange={handleSliderChange}
              style={{
                width: "80px",
                height: "4px",
                background: "#e1e5e9",
                outline: "none",
                borderRadius: "2px",
                cursor: "pointer"
              }}
              title={`${zoomLevel}%`}
            />
            <span style={{
              fontSize: "12px",
              fontWeight: "600",
              color: "#24292f",
              minWidth: "35px",
              textAlign: "center"
            }}>
              {zoomLevel}%
            </span>
          </div>
          
          <button
            onClick={handleZoomIn}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              borderRadius: "3px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#24292f",
              fontSize: "12px"
            }}
            onMouseEnter={(e) => e.target.style.background = "#f6f8fa"}
            onMouseLeave={(e) => e.target.style.background = "transparent"}
            title="Zoom In"
          >
            <FaSearchPlus size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default DefinedField; 